import { memo, useMemo } from "react";

import { parseGid } from "@shopify/hydrogen-react";

import { ArtworkVariantSelector } from "@components/VariantSelector/ArtworkVariantSelector.data";
import { ColorVariantSelector } from "@components/VariantSelector/ColorVariantSelector.data";
import { GenericVariantSelector } from "@components/VariantSelector/GenericVariantSelector.data";
import { StyleVariantSelector } from "@components/VariantSelector/StyleVariantSelector.data";
import { findVariants } from "@components/VariantSelector/utilities";
import { Image } from "@react/components/Image";
import { Text } from "@react/components/Text";
import {
  isArtworkOption,
  isColorOption,
  isStyleOption,
} from "@react/utilities";
import { type CartProduct, type CartProductVariant } from "@tablescape/state";
import { tw } from "classnames";

type Props = {
  activeProduct: CartProduct;
  context?: "productForm" | "productSelector" | "cart";
  displayUnavailableVariants: boolean;
  selectedVariant: CartProductVariant;
  setSelectedVariant: (variant: CartProductVariant["id"]) => void;
  showProductName?: boolean;
  showThumbnail?: boolean;
  size?: "tiny" | "mini" | "regular";
};

const styles = {
  manager: tw("mt-4", "flex", "gap-x-2", "md:gap-x-4"),
  shippingNote: tw("text-special"),
  image: (size?: "tiny" | "mini" | "regular" | "small" | "large") =>
    size !== "regular" && size !== "large"
      ? tw("h-16", "w-16", "md:h-18", "md:w-18")
      : "",
  selector: tw("flex", "flex-col", "gap-4"),
};

const VariantSelectorManagerBase = ({
  activeProduct,
  context,
  displayUnavailableVariants,
  selectedVariant,
  setSelectedVariant,
  showProductName = false,
  showThumbnail = false,
  size,
}: Props) => {
  const artworkVariants = useMemo(
    () =>
      findVariants({
        type: "artwork",
        variants: activeProduct.variants,
        selectedVariant,
        displayUnavailableVariants,
      }),
    [activeProduct.variants, selectedVariant, displayUnavailableVariants]
  );

  const colorVariants = useMemo(
    () =>
      findVariants({
        type: "color",
        variants: activeProduct.variants,
        selectedVariant,
        displayUnavailableVariants,
      }),
    [activeProduct.variants, selectedVariant, displayUnavailableVariants]
  );

  const styleVariants = useMemo(
    () =>
      findVariants({
        type: "style",
        variants: activeProduct.variants,
        selectedVariant,
        displayUnavailableVariants,
      }),
    [activeProduct.variants, selectedVariant, displayUnavailableVariants]
  );

  const genericVariants = useMemo(
    () =>
      findVariants({
        type: "generic",
        variants: activeProduct.variants,
        selectedVariant,
        displayUnavailableVariants,
      }),
    [activeProduct.variants, selectedVariant, displayUnavailableVariants]
  );

  return (
    <div
      className={styles.manager}
      data-mtr-product-category={activeProduct.productType}
      data-mtr-product-id={parseGid(activeProduct.id).id}
      data-mtr-product-name={activeProduct.title}
      data-mtr-product-price={selectedVariant.price.amount}
    >
      {showThumbnail && !!selectedVariant?.imageUrl && (
        <Image
          alt={selectedVariant.title}
          className={styles.image(size)}
          height={104}
          src={selectedVariant.imageUrl}
          width={104}
        />
      )}
      <div className={styles.selector}>
        {selectedVariant.selectedOptions.map((option) => {
          if (isColorOption(option)) {
            return (
              <ColorVariantSelector
                context={context}
                key={option.name}
                optionCategories={activeProduct.optionCategories}
                productName={activeProduct.title}
                selected={selectedVariant}
                setSelected={setSelectedVariant}
                showProductName={showProductName}
                size={size}
                variants={colorVariants}
              />
            );
          } else if (isArtworkOption(option)) {
            return (
              <ArtworkVariantSelector
                key={option.name}
                productName={activeProduct.title}
                selected={selectedVariant}
                setSelected={setSelectedVariant}
                showProductName={showProductName}
                size={size}
                variants={artworkVariants}
              />
            );
          } else if (isStyleOption(option)) {
            return (
              <StyleVariantSelector
                key={option.name}
                productName={activeProduct.title}
                selected={selectedVariant}
                setSelected={setSelectedVariant}
                showProductName={showProductName}
                variants={styleVariants}
              />
            );
          }

          return (
            <GenericVariantSelector
              key={option.name}
              productName={activeProduct.title}
              selected={selectedVariant}
              setSelected={setSelectedVariant}
              showProductName={showProductName}
              variants={genericVariants}
            />
          );
        })}
        {(context !== "productForm" || !selectedVariant.optionCategory) &&
          selectedVariant?.shippingNote && (
            <Text as="body2" className={styles.shippingNote} size="sm">
              {selectedVariant.shippingNote}
            </Text>
          )}
      </div>
    </div>
  );
};

export const VariantSelectorManager = memo(VariantSelectorManagerBase);
